import type { ManufacturerName, SensorType } from "../../../../shared.js";
import { DirectSubChannel, SensorPortCommunicationMode } from "../../../portConfigs.js";
import { SensorClusterConfig, SensorElementOutput } from "../types.js";

// ─── PSI → kPa conversion factor ─────────────────────────────────────────────
//
// All pressure transducers here output in PSI internally. Multiply by this
// constant to store values in kPa (the system's database unit for liquidPressure).
const PSI_TO_KPA = 6.89476;

// ─── Linear ADC pressure transducer helpers ──────────────────────────────────
//
// Factory calibration formula (from SensorConfigurationTemplates):
//   kPa = max(0, voltageMultiplier * V − voltageOffset) * pressureConversionFactor
//
// In terms of the pressureTransducer element params:
//   slopeKpaPerVolt = voltageMultiplier * pressureConversionFactor
//   offsetKpa       = −voltageOffset   * pressureConversionFactor

type LinearAdcPressureClusterParams = {
    /** Slope from the calibration formula. (L/min)/Hz analog: PSI per volt before unit conversion. */
    voltageMultiplier: number;
    /** Offset from the calibration formula. PSI at 0 V before unit conversion. */
    voltageOffset: number;
    manufacturerName: ManufacturerName;
    modelName: string;
    friendlyName: string;
    friendlyShortName: string;
    legacySensorType: SensorType;
};

function linearAdcPressureCluster({
    voltageMultiplier,
    voltageOffset,
    manufacturerName,
    modelName,
    friendlyName,
    friendlyShortName,
    legacySensorType,
}: LinearAdcPressureClusterParams): SensorClusterConfig<Record<string, never>> {
    const slopeKpaPerVolt = voltageMultiplier * PSI_TO_KPA;
    const offsetKpa = -voltageOffset * PSI_TO_KPA;

    const sensorElements: SensorElementOutput<"pressureTransducer">[] = [
        {
            sensorElementConfigKey: "pressureTransducer",
            parameters: { slopeKpaPerVolt, offsetKpa },
            legalConvertedRange: [0, null],
            subChannel: DirectSubChannel.ADC_5V,
        },
    ];

    return {
        communicationMode: SensorPortCommunicationMode.DIRECT,
        requiredSubChannels: [DirectSubChannel.ADC_5V],
        optionalSubChannels: [],
        isPermanentlyAttached: false,
        parameterConfigs: {},
        manufacturerName,
        modelName,
        friendlyName,
        friendlyShortName,
        generateSensorElements: () => sensorElements,
        migrationProperties: {
            legacySensorType,
            legacyParametersToClusterParameters: () => ({}),
            clusterParametersToLegacyParameters: () => ({}),
        },
    };
}

export const pressureTransducerClusterConfigs: Record<string, SensorClusterConfig<Record<string, never>>> = {
    // Keller 21Y — 0–150 PSI, 0.5–4.5 V
    v1transducer: linearAdcPressureCluster({
        voltageMultiplier: 37.5,
        voltageOffset: 18.75,
        manufacturerName: "Keller",
        modelName: "21Y",
        friendlyName: "Pressure Sensor",
        friendlyShortName: "Press. Sense",
        legacySensorType: "v1transducer",
    }),

    // Honeywell MIPAN1XX150PSAAX — 0–150 PSIG, 0.5–4.5 V. Same calibration as Keller 21Y.
    honeywell150Psig0_5VTransducer: linearAdcPressureCluster({
        voltageMultiplier: 37.5,
        voltageOffset: 18.75,
        manufacturerName: "Honeywell",
        modelName: "MIPAN1XX150PSAAX",
        friendlyName: "150 PSIG Pressure Sensor",
        friendlyShortName: "150 PSIG Press.",
        legacySensorType: "honeywell150Psig0_5VTransducer",
    }),

    // Honeywell MIPAN2XX060PAAAX — 0–45 PSIG (60 PSIA), 0.5–4.5 V.
    // Offset includes atmospheric subtraction: slope*Vmin + 14.696 atm = 15*0.5 + 14.696 = 22.196
    honeywell45Psig0_5VTransducer: linearAdcPressureCluster({
        voltageMultiplier: 15,
        voltageOffset: 22.196,
        manufacturerName: "Honeywell",
        modelName: "MIPAN2XX060PAAAX",
        friendlyName: "45 PSIG Pressure Sensor",
        friendlyShortName: "45 PSIG Press.",
        legacySensorType: "honeywell45Psig0_5VTransducer",
    }),
};
